import { ChatBot, NextPage, NextPageHead, OfficeLocationContextProvider, PageThemeContextProvider, PageView, STATE_CODES } from '@aspendental/shared-components-web';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { getBffCitiesByState } from '../../../services/bffFacilityCalls';
import { callsByBrand } from '../../../services/callsByBrand';
import { NOT_FOUND } from '@src/constants';
import { AppMetadataByAppNameDocument, AppPagesDataByAppNameDocument, LandingPageByAppNameWithPathDocument, PageFragment } from '@src/lib/__generated/sdk';
import { capitalizeFirstCharacter, formatStateBreadcrumb, getBeffeGraphQlUrl, getEnrichedPageContents, getRootLevelConfig, getUserCoordinates, setCacheHeader } from '@src/pages/utils/';
import choosePreview from '@src/pages/utils/choose-preview';
import DetermineIndexing from '@src/pages/utils/determine-indexing';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getDeviceTypeFromHeaders } from '@src/pages/utils/get-device-type-from-headers';
import getHostFromHeaders from '@src/pages/utils/get-host-from-headers';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import { TCity } from '@src/services/getLocations';
import { interpolateContent } from '@src/util/contentVariableInsertion';
const UpdateLivePreviewData = dynamic(() => import('@src/components/shared/providers/ContentfulLivePreview').then(mod => mod.UpdateLivePreviewData));
const StatePage = (props: Readonly<InferGetServerSidePropsType<typeof getServerSideProps>>) => {
  if (props.preview) {
    return <UpdateLivePreviewData InternalComponent={InternalStatePage} data={props} />;
  }
  return <InternalStatePage {...props} data-sentry-element="InternalStatePage" data-sentry-component="StatePage" data-sentry-source-file="index.page.tsx" />;
};
const InternalStatePage = (props: Readonly<InferGetServerSidePropsType<typeof getServerSideProps>>) => {
  const {
    appData,
    page,
    config,
    cities,
    breadcrumbsData,
    pageReferrer
  } = props;
  const pageData = page;
  const {
    url: logoUrl
  } = appData?.logo ?? {};
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    disableChatBot = false,
    analyticsContext,
    ogImage
  } = pageData;
  const pageTheme = page?.pageTheme || 'Primary';
  const {
    features,
    featureFlags,
    siteURL,
    logo
  } = config as BrandThemeConfig;
  return <PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-component="InternalStatePage" data-sentry-source-file="index.page.tsx">
			<OfficeLocationContextProvider cities={cities} data-sentry-element="OfficeLocationContextProvider" data-sentry-source-file="index.page.tsx">
				<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Office Details" data-sentry-element="PageView" data-sentry-source-file="index.page.tsx" />
				<NextPageHead path={path} title={title} indexed={DetermineIndexing(indexed, props.env, props.preview, featureFlags.doNotIndexWholeSite)} description={description} canonical={canonical} breadcrumbTitle={breadcrumbsData?.breadcrumbTitle} breadcrumbs={breadcrumbsData?.breadcrumbsCollection.items} originUrl={siteURL} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} data-sentry-element="NextPageHead" data-sentry-source-file="index.page.tsx" />
				<NextPage pageData={{
        ...pageData
      }} data-sentry-element="NextPage" data-sentry-source-file="index.page.tsx" />
				{!disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
			</OfficeLocationContextProvider>
		</PageThemeContextProvider>;
};
export const getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
  req,
  res,
  preview = false
}) => {
  const domain = getHostFromHeaders(req.headers);
  preview = choosePreview(domain, preview);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  // logger name is home because of backwards compatibility
  const {
    logger,
    brandConfig,
    gqlClient,
    appName
  } = getRootLevelConfig({
    req,
    preview,
    loggerName: 'home'
  });
  const facilityDetailsBasePath = brandConfig.features.otherApps.facilityDetailsBasePath;
  const facilityDetailsBasePathBreadCrumbTitle = brandConfig.features.otherApps.facilityDetailsBasePathBreadCrumbTitle;
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';

  // needs to have a state
  if (typeof params?.state !== 'string') {
    return NOT_FOUND;
  }
  const {
    stateName,
    userState,
    userStateCode
  } = getStateInfo(params.state);
  try {
    const [appPagesData, appMetadata, pageData, translations] = await Promise.all([gqlClient.query(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(LandingPageByAppNameWithPathDocument, {
      appName,
      path: `/${facilityDetailsBasePath}/[state]/`,
      preview
    }).toPromise(), getServerSideTranslations(locale)]);
    if (appPagesData.error) {
      logger.warn(`CONTENTFUL_WARN_DIRECTORY_STATE_PAGE: ${appPagesData.error}`);
    }
    if (appMetadata.error) {
      logger.error(`CONTENTFUL_ERROR: ${appMetadata.error}`);
    }
    const appData = {
      ...appPagesData.data?.appCollection?.items[0],
      ...appMetadata.data?.appCollection?.items[0]
    };

    // replace [env] with environment name for locationBarFindOfficeUrl
    // TODO: create a brand variable context to replace those variables from CMS in future
    if (appData && appData.header?.locationBarFindOfficeUrl) {
      const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
      appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace('[env]', brandSubdomain);
    }
    if (!appData) {
      return NOT_FOUND;
    }
    if (pageData.error) {
      // Reporting here exactly what the query above is looking for, replacement of [] vars not needed.
      logger.warn(`CONTENTFUL_WARN_DIRECTORY_STATE_DETAIL_PAGE: ${appPagesData.error} - /${facilityDetailsBasePath}/[state]/`);
    }
    const page = pageData.data.pageCollection?.items[0] as PageFragment;
    logger.info(`/${facilityDetailsBasePath}/${stateName}/`);
    if (!page) {
      logger.error(`COULD_NOT_FIND_STATE_PAGE /${facilityDetailsBasePath}/${stateName}/`);
      return NOT_FOUND;
    }
    const transformedServices = getConfigServices(brandConfig);
    const [cities, enrichedPageContents] = await Promise.all([getCities(appData, transformedServices, brandConfig, userStateCode, stateName), getEnrichedPageContents({
      page,
      gqlClient,
      preview,
      logger
    })]);
    if (!cities || cities.length === 0) {
      return NOT_FOUND;
    }
    const breadcrumbsData = {
      breadcrumbTitle: formatStateBreadcrumb(stateName),
      breadcrumbsCollection: {
        items: [{
          breadcrumbTitle: 'Home',
          path: '/'
        }, {
          breadcrumbTitle: capitalizeFirstCharacter(facilityDetailsBasePathBreadCrumbTitle),
          path: `/${facilityDetailsBasePath}/`
        }]
      }
    };
    setCacheHeader(res, preview);
    const pageStructure = {
      ...page,
      contentsCollection: {
        items: enrichedPageContents
      }
    };
    const interpolatedPageData = interpolateContent(pageStructure as any, [{
      value: userState,
      regex: /\[state\]/g
    }, {
      value: userState,
      regex: /\[city\], \[state\]/g
    }, {
      value: userState,
      regex: /\[location\]/g
    }, {
      value: stateName,
      regex: /\[state\-slug\]/g
    }]);
    return {
      props: {
        ...translations,
        preview,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        page: interpolatedPageData,
        appData,
        user: getUserCoordinates(req, logger),
        footerData: appData?.footer,
        breadcrumbsData,
        cities,
        deviceType,
        pageReferrer,
        stateCodeFromHeaders
      }
    };
  } catch (e) {
    logger.error(`OFFICE_DIRECTORY_STATE_PAGE_LOAD_FAILURE: ${e}`);
    return NOT_FOUND;
  }
};
export default StatePage;
function getStateInfo(stateName: string) {
  let userState = 'Your state';
  let userStateCode = '';
  if (stateName.length === 2) {
    // find state name from abbreviation
    userStateCode = stateName.toUpperCase();
    userState = STATE_CODES.find(stateData => stateData.code === userStateCode.toUpperCase())?.state || 'Your state';
  } else {
    // format state name directly from URL param
    userStateCode = STATE_CODES.find(stateData => stateData.code === userState.toUpperCase())?.state || 'Your state';
    userState = stateName.split('-').map(piece => capitalizeFirstCharacter(piece)).join(' ');
  }
  return {
    stateName,
    userState,
    userStateCode
  };
}
async function getCities(appData, transformedServices, brandConfig, userStateCode, stateName) {
  let cities: TCity[];
  if (appData.featureFlags.useFacilityBeffeDataSource) {
    const beffeGraphQlUrl = getBeffeGraphQlUrl(transformedServices);
    const citiesResponse = await getBffCitiesByState(brandConfig.bffBrandType, userStateCode, beffeGraphQlUrl);
    cities = citiesResponse?.data?.facilityCitiesByState as TCity[] || [];
  } else {
    cities = await callsByBrand[brandConfig.themeId].getCitiesByState(stateName);
  }
  return cities;
}